import { useApi } from "@/lib/api/useApi";
import { useMutate } from "@/lib/api/useMutate";
import { queryCache } from "@/lib/api/queryCache";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { addToCart } from "@/lib/api/cart";
import { cartQuery, productQuery } from "@/lib/api/queries";
import { Icon } from "@/components/store/Icon";
import { Reveal } from "@/components/motion/Reveal";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { useFormatters } from "@/lib/format";
import { useLocalizedContent } from "@/lib/localize";
import { SizeGuideModal } from "@/components/product/SizeGuideModal";
import { settingsApi } from "@/lib/api/arrex/settings";
import { BuyItWithItem, type BiwSelection } from "@/components/store/BuyItWithItem";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { imgUrl } from "@/lib/format";


export function titleize(slug: string) {
  return slug
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function ProductPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useFormatters();
  const { productName, productCategory, colorName, sizeName } = useLocalizedContent();
  const { slug } = useParams();
  const pq = productQuery(slug);
  const { data: product } = useApi(pq.queryKey, pq.queryFn);

  // Fetch Buy It With products
  const { data: buyItWithData } = useApi(
    ["buy-it-with", slug],
    () => settingsApi.buyItWith(slug ?? ""),
    { enabled: !!slug }
  );
  const buyItWithProducts = Array.isArray(buyItWithData) ? buyItWithData : (buyItWithData as any)?.items || [];

  const [activeImage, setActiveImage] = useState(0);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [biwSelections, setBiwSelections] = useState<Record<string, BiwSelection | null>>({});

  const setBiwSelection = (productId: string, sel: BiwSelection | null) => {
    setBiwSelections((prev) => ({ ...prev, [productId]: sel }));
  };

  const colors = product?.colors ?? [];
  const sizes = product?.sizes ?? [];
  const activeColorObj = colors.find(
    (c: any) =>
      c.name === color ||
      c.nameEn === color ||
      c.name_en === color ||
      c.nameAr === color ||
      c.name_ar === color ||
      c.id === color
  ) as any;

  const colorImages = activeColorObj?.images?.length ? activeColorObj.images : activeColorObj?.product_color_images?.length ? activeColorObj.product_color_images : null;
  const mappedColorImages = colorImages?.map((img: any) => typeof img === 'string' ? img : img.url || img.image_url);
  const images: string[] = mappedColorImages?.length ? mappedColorImages : (product?.images?.length ? product.images : [""]);

  const isColorMatch = (sColor: any, targetColor: string, colorObj?: any) => {
    if (!sColor) return true;
    const sStr = String(sColor).trim().toLowerCase();
    const tStr = String(targetColor || '').trim().toLowerCase();
    if (sStr === tStr) return true;
    if (colorObj) {
      if (colorObj.id && String(colorObj.id).toLowerCase() === sStr) return true;
      if (colorObj.name && String(colorObj.name).trim().toLowerCase() === sStr) return true;
      if (colorObj.nameEn && String(colorObj.nameEn).trim().toLowerCase() === sStr) return true;
      if (colorObj.name_en && String(colorObj.name_en).trim().toLowerCase() === sStr) return true;
      if (colorObj.nameAr && String(colorObj.nameAr).trim().toLowerCase() === sStr) return true;
      if (colorObj.name_ar && String(colorObj.name_ar).trim().toLowerCase() === sStr) return true;
    }
    return false;
  };

  const getStockQtyFor = (sizeOption: string) => {
    if (activeColorObj?.stockMap && typeof activeColorObj.stockMap === 'object') {
      if (sizeOption in activeColorObj.stockMap) {
        return Number(activeColorObj.stockMap[sizeOption] ?? 0);
      }
    }
    if (!product?.stock || !Array.isArray(product.stock) || product.stock.length === 0) {
      return 999;
    }
    const colorStock = product.stock.filter((s: any) =>
      isColorMatch(s.color || s.colorEn || s.colorAr || s.colorId, color, activeColorObj)
    );
    if (colorStock.length === 0) {
      const sizeStock = product.stock.filter((s: any) => s.size === sizeOption);
      if (sizeStock.length === 0) return 999;
      return sizeStock.reduce((max: number, s: any) => Math.max(max, Number(s.qty ?? s.stock_qty ?? 0)), 0);
    }
    const match = colorStock.find((s: any) => s.size === sizeOption);
    return match ? Number(match.qty ?? match.stock_qty ?? 0) : 0;
  };

  const checkSizeInStock = (sizeOption: string) => {
    if (product?.status === 'archived') return false;
    return getStockQtyFor(sizeOption) > 0;
  };

  const isStockAvailable = size ? checkSizeInStock(size) : (sizes.length > 0 ? sizes.some(checkSizeInStock) : true);

  useEffect(() => {
    if (!product) return;
    const initialColor = product.colors?.[0]?.name ?? "";
    setColor(initialColor);
    const initialColorObj = product.colors?.[0];
    const initialSizes = product.sizes ?? [];
    const firstInStock = initialSizes.find((s: string) => {
      if (initialColorObj?.stockMap && s in initialColorObj.stockMap) {
        return Number(initialColorObj.stockMap[s] ?? 0) > 0;
      }
      return true;
    });
    setSize(firstInStock || initialSizes[0] || "");
    setActiveImage(0);
  }, [product]);

  useEffect(() => {
    setActiveImage(0);
    if (activeColorObj?.stockMap && size && Number(activeColorObj.stockMap[size] ?? 0) === 0) {
      const nextAvailable = sizes.find((s: string) => Number(activeColorObj.stockMap[s] ?? 0) > 0);
      if (nextAvailable) setSize(nextAvailable);
    }
  }, [color]);

  const addMutation = useMutate(
    async () => {
      const chosenColor = activeColorObj?.nameEn || activeColorObj?.name_en || color;
      await addToCart({ productId: product?.id ?? "", color: chosenColor, size });
      for (const key of Object.keys(biwSelections)) {
        const sel = biwSelections[key];
        if (sel) {
          await addToCart({
            productId: key,
            color: sel.colorName_en || "",
            size: sel.size || "",
          });
        }
      }
    },
    {
      onSuccess: () => {
        if (product) {
          queryCache.invalidate(cartQuery().queryKey);
          toast.success(t("common.addedToBag", { name: productName(product.name) }));
          setBiwSelections({});
        }
      },
      onError: (error: Error) => toast.error(error.message),
    }
  );

  if (!product) {
    return (
      <StoreLayout>
        <main className="pt-10 pb-section-gap-desktop max-w-7xl mx-auto px-grid-margin min-h-[50vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </main>
      </StoreLayout>
    );
  }

  // Block access to coming-soon products
  if ((product as any).status === 'coming_soon') {
    return (
      <StoreLayout>
        <main className="pt-10 pb-section-gap-desktop max-w-7xl mx-auto px-grid-margin min-h-[50vh] flex flex-col items-center justify-center gap-6">
          <div className="text-center space-y-3">
            <span className="inline-block bg-secondary text-white text-sm font-bold px-4 py-2 rounded-full uppercase tracking-widest">
              {t('admin.common.comingSoon', { defaultValue: 'Coming Soon' })}
            </span>
            <h1 className="font-display text-3xl font-bold">{productName(product.name)}</h1>
            <p className="text-on-surface-variant">{t('product.comingSoonDesc', { defaultValue: 'This product is not available yet. Check back soon!' })}</p>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 me-2" />{t('product.back', { defaultValue: 'Back' })}
          </Button>
        </main>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <main className="pt-10 pb-section-gap-desktop max-w-7xl mx-auto px-grid-margin">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-6 cursor-pointer hover:bg-surface-container-low transition-colors">
          <ArrowLeft className="w-4 h-4 me-2" />{t('product.back', { defaultValue: 'Back' })}
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-gutter items-start">
          <Reveal className="md:col-span-1 lg:col-span-7 flex flex-col gap-4">
            {/* Main image */}
            <div className="relative w-full aspect-square overflow-hidden rounded-lg bg-surface-container-low shadow-sm">
              <AnimatePresence mode="popLayout" initial={false}>
                {imgUrl(images[activeImage]) ? (
                  <motion.img
                    key={`${color}-${activeImage}-${images[activeImage]}`}
                    src={imgUrl(images[activeImage])}
                    alt={productName(product.name)}
                    initial={{ opacity: 0, scale: 1.08, filter: "blur(16px)" }}
                    animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0, scale: 1.03, filter: "blur(12px)" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-on-surface-variant/40">
                    <Icon name="image" className="text-[48px]" />
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Thumbnails — horizontal row below the main image, always visible */}
            {images.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {images.map((image: string, index: number) => (
                  <motion.button
                    key={`${color}-${image}-${index}`}
                    type="button"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => setActiveImage(index)}
                    className={`aspect-square cursor-pointer overflow-hidden rounded-lg transition-all ${
                      index === activeImage
                        ? "ring-2 ring-primary ring-offset-1"
                        : "opacity-70 hover:opacity-100 ring-1 ring-outline-variant"
                    }`}
                  >
                    <img
                      src={imgUrl(image) || undefined}
                      alt={t("product.view", { name: productName(product.name), index: index + 1 })}
                      className="w-full h-full object-cover"
                    />
                  </motion.button>
                ))}
              </div>
            )}
          </Reveal>

          <Reveal className="md:col-span-1 lg:col-span-5 flex flex-col gap-5 md:gap-8">
            <div>
              <span className="text-label-sm font-label-sm text-secondary uppercase tracking-[0.15em] mb-2 block">
                {productCategory((product as any).category?.label ?? product.category)}
              </span>
              <h1 className="font-display-lg text-[clamp(24px,4vw,36px)] text-deep-chocolate mb-3 font-semibold uppercase tracking-wider">
                {productName(product.name)}
              </h1>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                {product.compareAtPrice && product.compareAtPrice > 0 && Math.max(product.price, product.compareAtPrice) > Math.min(product.price, product.compareAtPrice) ? (
                  <>
                    <p className="font-display text-body-lg text-red-600 line-through whitespace-nowrap">
                      {formatPrice(Math.max(product.price, product.compareAtPrice))}
                    </p>
                    <p className="font-display text-headline-sm text-primary font-bold whitespace-nowrap">
                      {formatPrice(Math.min(product.price, product.compareAtPrice))}
                    </p>
                    <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                      -{Math.round(((Math.max(product.price, product.compareAtPrice) - Math.min(product.price, product.compareAtPrice)) / Math.max(product.price, product.compareAtPrice)) * 100)}% OFF
                    </span>
                  </> 
                ) : (
                  <p className="font-display text-headline-sm text-secondary font-bold whitespace-nowrap">
                    {formatPrice(product.price)}
                  </p>
                )}
              </div>
            </div>

            {(product as any).description && (
              <p className="text-body-md text-on-surface-variant/90 leading-relaxed font-light">
                {productName((product as any).description)}
              </p>
            )}

            {colors.length > 0 && (
              <div>
                <span className="text-label-md font-label-md text-on-surface-variant/80 uppercase tracking-widest mb-4 block">
                  {t("common.colour")}:{" "}
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={color}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="text-on-surface font-semibold inline-block"
                    >
                      {colorName(color)}
                    </motion.span>
                  </AnimatePresence>
                </span>
                <div className="flex gap-3">
                  {colors.map((c: any) => (
                    <motion.button
                      key={c.name}
                      type="button"
                      onClick={() => setColor(c.name)}
                      whileHover={{ scale: 1.12 }}
                      whileTap={{ scale: 0.92 }}
                      animate={{
                        scale: c.name === color ? 1.1 : 1,
                        borderColor: c.name === color ? "var(--primary)" : "transparent",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 220,
                        damping: 18,
                      }}
                      className={`w-10 h-10 rounded-full border-2 cursor-pointer ${
                        c.name === color ? "shadow-md" : "hover:border-outline-variant"
                      }`}
                      style={{ backgroundColor: c.hex }}
                      title={colorName(c.name)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-label-md font-label-md text-on-surface-variant/80 uppercase tracking-widest">
                  {t("product.selectSize")}
                </span>
                {product?.sizeGuideId && product?.showSizeGuide && (
                  <button
                    type="button"
                    onClick={() => setShowSizeGuide(true)}
                    className="text-label-sm font-label-sm text-secondary hover:underline underline-offset-4 cursor-pointer"
                  >
                    {t("product.sizeGuide")}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-4 gap-3">
                {sizes.map((option) => {
                  const sizeInStock = checkSizeInStock(option);
                  return (
                  <button
                    key={option}
                    type="button"
                    disabled={!sizeInStock}
                    onClick={() => setSize(option)}
                    className={`py-3 text-label-md font-label-md transition-colors ${
                      option === size
                        ? "border border-primary bg-surface-container-low"
                        : "border border-outline-variant hover:border-primary"
                    } ${!sizeInStock ? "line-through text-on-surface-variant/40 cursor-not-allowed bg-muted/20" : ""}`}
                  >
                    {sizeName(option)} {!sizeInStock ? " (X)" : ""}
                  </button>
                )})}
              </div>
            </div>

            {buyItWithProducts.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider mb-2 font-semibold text-on-surface-variant flex items-center gap-1.5">
                  <span className="text-secondary">✦</span> {t('product.buyItWith', { defaultValue: 'Buy It With' })}
                </p>
                <div className="space-y-2.5">
                  {buyItWithProducts.map((item: any) => (
                    <BuyItWithItem
                      key={item.id}
                      product={item}
                      selection={biwSelections[item.id] || null}
                      onSelectionChange={(sel) => setBiwSelection(item.id, sel)}
                    />
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              disabled={addMutation.isPending || !isStockAvailable}
              onClick={() => addMutation.mutate()}
              className="w-full py-5 bg-secondary text-white font-label-md text-label-md uppercase tracking-[0.2em] rounded-lg shadow-lg shadow-secondary/20 hover:bg-[#5d4201] transition-all duration-300 transform hover:-translate-y-0.5 mb-0 disabled:opacity-60 cursor-pointer"
            >
              {addMutation.isPending ? t("common.adding") : isStockAvailable ? t("common.addToCart") : t("common.outOfStock")}
            </button>
            
            <div className="flex justify-center mb-6">
              <Link 
                to="/returns" 
                className="text-label-sm font-label-sm text-on-surface-variant hover:text-primary transition-colors flex items-center gap-1.5 uppercase tracking-widest"
              >
                <Icon name="swap_horiz" className="text-lg" />
                {t("returns.title", { defaultValue: "Shipping & Returns Policy" })}
              </Link>
            </div>
          </Reveal>
        </div>
      </main>

      {product?.sizeGuideId && product?.showSizeGuide && (
        <SizeGuideModal
          open={showSizeGuide}
          onOpenChange={setShowSizeGuide}
          sizeGuideId={product.sizeGuideId}
        />
      )}
    </StoreLayout>
  );
}

export default ProductPage;
